// PKCE (RFC 7636, S256) for the step-up authorization-code flow, required by
// oauth2ng per Dalia. The verifier is generated per flow; its S256 challenge goes
// on /authorize and the verifier itself is presented at /token.

export interface PkcePair {
  codeVerifier: string;
  codeChallenge: string;
}

// base64url (RFC 4648 §5) of raw bytes, without padding — the encoding PKCE
// mandates for both the verifier and the challenge.
const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// 32 random bytes -> ~43-char base64url verifier (within the 43-128 spec range).
export const generatePkce = async (): Promise<PkcePair> => {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  const codeVerifier = base64UrlEncode(randomBytes);

  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier),
  );
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));

  return { codeVerifier, codeChallenge };
};

export const PKCE_CODE_CHALLENGE_METHOD = 'S256';
